import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  isReadOnlyRole,
  type AssetStateResponse,
  type FacilityDashboardEntry,
  type MaintenanceItemSummary,
  type ObservationPhaseVocabulary,
  type ReportPhoto,
  type ReportPhotoGroup,
} from "@bitacorapro/api-types";
import { formatRelativeAgo } from "@bitacorapro/shared/relative-time";
import { EvidenciaCell } from "../components/EvidenciaCell";
import { EstadoPills } from "../components/ObservacionPills";
import { Icon } from "../components/icons";
import { MaintenanceServicePopover } from "../components/MaintenanceServicePopover";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { PhotoGallery } from "../components/PhotoGallery";
import { QueryBoundary } from "../components/QueryBoundary";
import { Skel } from "../components/Skel";
import { StatusPill } from "../components/StatusPill";
import {
  useAssetMaintenanceItems,
  useAssetState,
  useFacilityDashboard,
  useRecipe,
  useVisitPhotos,
} from "../lib/api/queries";
import { canMarkMaintenanceServiced } from "../lib/capabilities";
import { useQueryView } from "../lib/loading/useQueryView";
import { backOriginState } from "../lib/backOrigin";
import { useSession } from "../lib/auth/useSession";
import { entryState, isEntryOpen } from "../lib/postventa/entryThread";
import {
  isClosedConforme,
  isPositiveGroup,
  isProblemGroup,
  isUnattributedGroup,
} from "../lib/photoGroups";
import {
  closedActaAdjective,
  daysSince,
  deriveMaintUrgency,
  deriveStoreStatus,
  fmtDate,
  fmtMaintDate,
  observationHeadline,
  sevLabel,
  sevPillClass,
  storeSubtitle,
  storeTitle,
  type MaintUrgency,
} from "../lib/util";
import {
  DetalleTiendaBodySkeleton,
  DetalleTiendaHeaderSkeleton,
  MaintenanceRowsSkeleton,
  SectionStateSkeleton,
  VisitPhotosSkeleton,
} from "./DetalleTienda.skeleton";

// Store-detail carousel copy (2026-09-25 founder review with Dani +
// Mario). Recipe-driven noun per ADR-0111 (facility says «reporte», other
// verticals keep «acta»); no "contacta a tu supervisor" because the same
// screen is read by jefe de tienda, supervisor and manager, so no single
// addressee is right (ux-expert). Since ADR-0137 the carousel shows the
// whole acta (conformes and observaciones), not only observation photos.
function demoNoActaCopy(report: string): string {
  return `Aún no hay ningún ${report} para esta tienda. Las fotos del estado de la tienda van a aparecer aquí en cuanto se registre el primero.`;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

// «{fecha} · {n} fotos · {c} conformes · {o} con observación» (ADR-0137).
// The verdict counts only appear when some photo is attributed to an
// item — an acta older than per-item capture has none to count. With
// server groups the counts are CARDS (one per item), never photos:
// «{fecha} · {o} observaciones · {c} conformes · {s} sin evaluar · {n} fotos».
function galleryMeta(
  date: string,
  photos: ReportPhoto[],
  groups: readonly ReportPhotoGroup[]
): string {
  if (groups.length > 0) {
    const obs = groups.filter((g) => isProblemGroup(g)).length;
    const ok = groups.filter((g) => isPositiveGroup(g)).length;
    const na = groups.filter(
      (g) => !isProblemGroup(g) && !isPositiveGroup(g) && g.itemConformance === "not_applicable"
    ).length;
    const unrated = groups.length - obs - ok - na;
    const parts = [date, plural(obs, "observación", "observaciones")];
    if (ok > 0) parts.push(plural(ok, "conforme", "conformes"));
    if (na > 0) parts.push(`${na} no aplica`);
    if (unrated > 0) parts.push(`${unrated} sin evaluar`);
    parts.push(plural(photos.length, "foto", "fotos"));
    return parts.join(" · ");
  }
  const parts = [date, plural(photos.length, "foto", "fotos")];
  if (photos.some((p) => p.verdict !== null)) {
    const ok = photos.filter((p) => p.verdict?.tone === "ok").length;
    const obs = photos.filter((p) => p.verdict !== null && p.verdict.tone !== "ok").length;
    parts.push(plural(ok, "conforme", "conformes"), `${obs} con observación`);
  }
  return parts.join(" · ");
}

// «Estado por sección» (ADR-0137): one chip per section of the store's
// latest terminal acta (the API returns at most one source, whatever its
// checklist template), conformance = conformes ÷ (conformes + con
// observación). Sections with nothing reviewed (null) are hidden, never
// shown as 0 % or 100 %; with no section left the strip doesn't render.
//
// The percentages are frozen at that visit while its observations keep
// moving; the note dates them, and says so outright once some finding of
// that visit has since been closed.
function reviewedSections(state: AssetStateResponse | undefined) {
  return (state?.sources[0]?.sections ?? []).filter((s) => s.conformancePct !== null);
}

function SectionStateStrip({
  state,
  visitDate,
  someResolved,
}: {
  state: AssetStateResponse | undefined;
  visitDate: string;
  someResolved: boolean;
}) {
  const sections = reviewedSections(state);
  if (sections.length === 0) return null;
  return (
    <div className="vsec">
      <div className="vsec__title">Estado por sección</div>
      <p className="vgal__note">
        Según la visita del {visitDate}.
        {someResolved &&
          " Algunos hallazgos de esta visita ya fueron resueltos. El porcentaje refleja lo que se vio ese día."}
      </p>
      <ul className="vsec__list" aria-label="Estado por sección">
        {sections.map((s) => {
          const reviewed = s.counts.positive + s.counts.negative;
          return (
            <li key={s.key} className="vsec__item">
              <span className="vsec__name">{s.label}</span>
              <span className="vsec__pct">{s.conformancePct}%</span>
              <span className="vsec__n mono" aria-hidden="true">
                {s.counts.positive}/{reviewed}
              </span>
              <span className="sr-only">
                {`${s.counts.positive} de ${reviewed} ${reviewed === 1 ? "ítem conforme" : "ítems conformes"}`}
              </span>
              <span className="vsec__bar" aria-hidden="true">
                <span className="vsec__fill" style={{ width: `${s.conformancePct}%` }} />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function timelineTone(score: number | null): string {
  if (score === null) return "";
  if (score >= 4.3) return "timeline__item--ok";
  if (score >= 3.7) return "timeline__item--warn";
  return "timeline__item--crit";
}

export function DetalleTienda() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Observación links carry this tienda as the back origin so the
  // detail's "Volver" returns here, not to the full list (lib/backOrigin.ts).
  const backState = backOriginState(`/tiendas/${id}`, "Volver a la tienda");
  // ADR-0166: one view per query — loading / error / ready are
  // distinct, so a failed or disabled query never hangs on a skeleton.
  const dashboardQ = useFacilityDashboard();
  const { data } = dashboardQ;
  const view = useQueryView(dashboardQ);
  const { data: recipe } = useRecipe();
  const maintenanceQ = useAssetMaintenanceItems(id);
  const maintenanceData = maintenanceQ.data;
  const maintenanceView = useQueryView(maintenanceQ);
  // Which item's "registrar mantención" popover is open, if any. A ref
  // registry (not React state) holds the check-button DOM nodes so the
  // popover can anchor to — and, on close, useDialogFocus can restore
  // focus to — the exact control that opened it, even after the list
  // re-sorts underneath it (React reconciles by item.id, so the same
  // button node survives a re-sort, it just moves position).
  const [servicingItem, setServicingItem] = useState<{ id: string; label: string } | null>(null);
  const checkButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const [toast, setToast] = useState<string | null>(null);
  const session = useSession();
  // Server re-derives scope per request; this only decides whether to
  // render an affordance the caller could actually use. A read-only role
  // (brand_manager, ADR-0164) gets 403 ROLE_READ_ONLY on every write, so
  // the check button is hidden for it even if the writer set widens.
  const canMarkMaintenance = session
    ? canMarkMaintenanceServiced(session.organization.role) &&
      !isReadOnlyRole(session.organization.role)
    : false;

  const tienda = useMemo(() => data?.byAsset.find((a) => a.id === id) ?? null, [data, id]);
  const maintenanceItems = maintenanceData?.items ?? [];
  // Client-side sort: never-serviced first (an unknown state is at least
  // as actionable as a known-bad one, and the panel has no other way to
  // surface it — founder-confirmed), then crit, then warn, then ok, tied
  // items sorted by dueDate then by label so the never-serviced group
  // (no dueDate to sort by) has a stable order across renders.
  const sortedMaintenanceItems = useMemo(() => {
    function urgencyRank(u: MaintUrgency): number {
      if (u.status === "never-serviced") return 0;
      return { crit: 1, warn: 2, ok: 3 }[u.tone];
    }
    return maintenanceItems
      .map((item) => ({ item, urgency: deriveMaintUrgency(item) }))
      .sort((a, b) => {
        const rankDiff = urgencyRank(a.urgency) - urgencyRank(b.urgency);
        if (rankDiff !== 0) return rankDiff;
        if (a.urgency.status === "scheduled" && b.urgency.status === "scheduled") {
          return a.urgency.dueDate.getTime() - b.urgency.dueDate.getTime();
        }
        return a.item.label.localeCompare(b.item.label);
      });
  }, [maintenanceItems]);

  const zoneName = useMemo(() => {
    if (!data || !tienda?.assetGroupId) return null;
    return data.byAssetGroup.find((g) => g.id === tienda.assetGroupId)?.name ?? null;
  }, [data, tienda]);

  const openObs = useMemo(() => {
    if (!data || !tienda) return [] as FacilityDashboardEntry[];
    // ADR-0134: «abierta» is the thread's phase (not closed by conformity,
    // not anulada), never the retired delegation column.
    return data.entries.filter((e) => e.assetId === tienda.id && isEntryOpen(e, recipe));
  }, [data, tienda, recipe]);

  const visits = useMemo(
    () => (data && tienda ? (data.byAssetVisits[tienda.id] ?? []) : []),
    [data, tienda]
  );

  // Gallery scope: the single most-recent closed acta, matching the canvas
  // mockup ("Estado visual · último reporte") — not every visit's photos
  // combined. `visits` is already newest-first.
  const latestVisit = visits[0];

  // Parent-owned toast, not rendered inside the row — same WCAG 4.1.3
  // rationale as AssignDrawer's onSent (accessibility-expert).
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function onMaintenanceServiced(label: string) {
    setServicingItem(null);
    setToast(`Mantención registrada · ${label}`);
  }
  // ADR-0137: the carousel is the latest acta's own photos, each with the
  // supervisor verdict of the checklist item it was attached to (problems
  // first, then conformes; server-ordered). Loose observations and
  // standalone findings are not part of the acta and never appear here.
  // Enabled only once the aggregate names a latest visit, so it waits on
  // the dashboard query (useQueryView's whole-ancestor-chain rule).
  const photosQ = useVisitPhotos(latestVisit?.reportId);
  const photosView = useQueryView(photosQ, { waitingOn: [dashboardQ] });
  const assetStateQ = useAssetState(id);

  // Recipe noun for the carousel copy («reporte» on facility).
  const demoReportNoun = (recipe?.labels.report ?? "Reporte").toLowerCase();

  if (view.kind === "loading") {
    // The header's identity (unit, address, brand) is data, so the
    // header frame skeletonizes inside the same busy container as the
    // body; panel titles and the gallery label are static and render.
    return (
      <div className="page">
        <PageSkeleton label="Cargando la tienda…" visible={view.visible}>
          <DetalleTiendaHeaderSkeleton />
          <DetalleTiendaBodySkeleton reportNoun={demoReportNoun} />
        </PageSkeleton>
        {view.slow && (
          <div className="notice-banner notice-banner--action">
            <span>
              <strong>Esto está tardando más de lo normal.</strong> Revisa tu conexión. Seguimos
              intentando.
            </span>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => void dashboardQ.refetch()}
            >
              Reintentar
            </button>
          </div>
        )}
      </div>
    );
  }
  if (view.kind !== "ready") {
    // error — or a disabled/empty aggregate, which this route never
    // configures; either way nothing is on its way, so never a skeleton.
    const offline = view.kind === "error" && view.offline;
    return (
      <div className="page">
        <PageError
          title={offline ? "Sin conexión" : "No pudimos cargar la tienda"}
          description={
            offline
              ? "Esta información aún no está guardada en este dispositivo. Conéctate para verla."
              : "Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
          }
          onRetry={view.kind === "error" ? view.retry : () => void dashboardQ.refetch()}
        />
      </div>
    );
  }
  if (!tienda) {
    return (
      <div className="page-pad">
        <h1 className="page-header__title">Tienda no encontrada</h1>
        <p className="page-header__sub">
          El id <code>{id}</code> no corresponde a ninguna tienda de tu organización.
        </p>
        <Link to="/tiendas" className="btn btn--secondary">
          <Icon.Arrow s={14} /> Volver a tus tiendas
        </Link>
      </div>
    );
  }

  const photosData = photosView.kind === "ready" ? photosView.data : undefined;
  const visitPhotos = photosData?.photos ?? [];
  const photoGroups = photosData?.groups ?? [];
  // Every photo unattributed: the acta predates per-item capture
  // (ADR-0088), so none can carry a verdict — say so once instead of
  // implying nothing was evaluated. With groups: no card is an item, has
  // a verdict or carries an observation.
  const legacyOnly =
    visitPhotos.length > 0 &&
    (photoGroups.length > 0
      ? photoGroups.every(
          (g) => isUnattributedGroup(g) && g.verdict === null && g.observation === null
        )
      : visitPhotos.every((p) => p.verdict === null && p.itemLabel === null));
  const vocab: ObservationPhaseVocabulary | undefined = recipe;
  // False while the photos load or fail: no groups to judge yet.
  const someResolved =
    photosView.kind === "ready" && photoGroups.some((g) => isClosedConforme(g, vocab));

  const status = deriveStoreStatus({
    openFindingsCount: tienda.openFindingsCount,
    lastVisit: tienda.lastVisit,
    idleAfterDays: recipe?.alDiaThresholdDays,
  });

  return (
    <>
      <div className="page">
        <div className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow mono">
              {storeSubtitle(tienda) ?? "—"}
              {tienda.brandName ? ` · ${tienda.brandName}` : ""}
              {zoneName ? ` · ${zoneName}` : ""}
            </div>
            <h1 className="page-header__title">{storeTitle(tienda)}</h1>
            <div className="detail-chip-row">
              <StatusPill status={status} />
              {zoneName && (
                <span className="st-pill st-pill--idle">
                  <Icon.Pin s={12} /> {zoneName}
                </span>
              )}
              {tienda.brandName && (
                <span className="st-pill st-pill--idle">{tienda.brandName}</span>
              )}
            </div>
          </div>
        </div>

        <div className="vgal">
          <div className="vgal__bar">
            <div className="vgal__label">Estado del último {demoReportNoun}</div>
            <div className="vgal__meta mono">
              {!latestVisit || photosView.kind === "error" ? (
                "—"
              ) : photosView.kind === "loading" ? (
                <Skel variant="text" width="22ch" />
              ) : (
                galleryMeta(fmtDate(latestVisit.signedAt), visitPhotos, photoGroups)
              )}
            </div>
          </div>
          {!latestVisit ? (
            <div className="vgal__empty">
              <Icon.Photo s={16} />
              <span>{demoNoActaCopy(demoReportNoun)}</span>
            </div>
          ) : photosView.kind === "loading" ? (
            // A section of the loaded page: aria-busy only, no second
            // status announcement (the page already made its one).
            <PageSkeleton
              label={`Cargando las fotos del ${demoReportNoun}…`}
              visible={photosView.visible}
              announce={false}
              kind="section"
            >
              <VisitPhotosSkeleton />
            </PageSkeleton>
          ) : photosView.kind === "error" ? (
            <PageError
              title={`No pudimos cargar las fotos del ${demoReportNoun}`}
              description="Revisa tu conexión e intenta de nuevo."
              onRetry={photosView.retry}
              autoFocus={false}
            />
          ) : (
            <>
              {legacyOnly && (
                <p className="vgal__note">
                  Este {demoReportNoun} es anterior a la captura por ítem.
                </p>
              )}
              <PhotoGallery
                photos={visitPhotos}
                groups={photoGroups}
                vocab={vocab}
                // ADR-0169 — per-photo review; `enabled: false` (flag off,
                // or a report type without the primitive) changes nothing.
                review={
                  photosData
                    ? { reportId: photosData.reportId, ...photosData.photoReview }
                    : undefined
                }
                variant="hero"
                emptyText={`El último ${demoReportNoun} de esta tienda no tiene fotos registradas.`}
              />
            </>
          )}
          {latestVisit ? (
            // The strip belongs to the latest acta: while its state loads
            // it holds its place; with no section reviewed it renders
            // nothing (hidden, never 0 % or 100 %).
            <QueryBoundary
              query={assetStateQ}
              label="el estado por sección"
              kind="section"
              skeleton={<SectionStateSkeleton />}
              isEmpty={(state) => reviewedSections(state).length === 0}
              empty={null}
            >
              {(state) => (
                <SectionStateStrip
                  state={state}
                  visitDate={fmtDate(latestVisit.signedAt)}
                  someResolved={someResolved}
                />
              )}
            </QueryBoundary>
          ) : (
            // No acta yet: nothing to wait for, so no placeholder either.
            <SectionStateStrip state={assetStateQ.data} visitDate="—" someResolved={false} />
          )}
        </div>

        <div className="detail" style={{ display: "block" }}>
          <div className="detail__main">
            {/* Mantenciones + Historial de visitas: shown again 2026-09-25
              (founder decision); the hero carousel above is permanent
              (founder decision 2026-10-22). */}
            <div className="grid-2" style={{ marginBottom: 20, alignItems: "start" }}>
              <div className="panel">
                <div className="panel__head">
                  <div className="panel__title">Mantenciones</div>
                  <div className="panel__sub mono">
                    {maintenanceView.kind === "loading" ? (
                      <Skel variant="text" width="6ch" />
                    ) : maintenanceView.kind === "error" ? (
                      "—"
                    ) : (
                      <>
                        {maintenanceItems.length} ítem
                        {maintenanceItems.length === 1 ? "" : "s"}
                      </>
                    )}
                  </div>
                </div>
                <div className="panel__body--flush">
                  {maintenanceView.kind === "loading" ? (
                    <PageSkeleton
                      label="Cargando las mantenciones…"
                      visible={maintenanceView.visible}
                      announce={false}
                      kind="section"
                    >
                      <MaintenanceRowsSkeleton />
                    </PageSkeleton>
                  ) : maintenanceView.kind === "error" ? (
                    <PageError
                      title="No pudimos cargar las mantenciones"
                      description="Revisa tu conexión e intenta de nuevo."
                      onRetry={maintenanceView.retry}
                      autoFocus={false}
                    />
                  ) : sortedMaintenanceItems.length === 0 ? (
                    <div className="empty">
                      <div className="empty__title">Sin mantenciones configuradas</div>
                      <div className="empty__sub">
                        Esta tienda todavía no tiene ítems de mantención asignados. Cuando se
                        configuren extintores, equipos u otras mantenciones periódicas, vas a poder
                        hacerles seguimiento aquí.
                      </div>
                    </div>
                  ) : (
                    sortedMaintenanceItems.map(({ item, urgency }) => (
                      <div key={item.id} className="maint__row">
                        {/* Non-interactive lead — unlike the checklist-template
                          row it replaces, a maintenance item has no bloque/
                          item structure to drill into (ADR-0083 Decision 6),
                          so this is a <div>, not a <button>: a hover
                          highlight with nothing behind it is a false
                          affordance. */}
                        <div className="maint__row-main">
                          <div className="maint__lead">
                            <div className="maint__name">{item.label}</div>
                            <div className="maint__cadence">{maintenanceServiceLabel(item)}</div>
                          </div>
                          {urgency.status === "scheduled" ? (
                            <div className="maint__due">
                              <div className="maint__date">{fmtMaintDate(urgency.dueDate)}</div>
                              <span className={`st-pill st-pill--${urgency.tone}`}>
                                {urgency.daysLabel}
                              </span>
                            </div>
                          ) : (
                            <div className="maint__due">
                              <div className="maint__date">—</div>
                              <span className="st-pill st-pill--idle">Sin registro</span>
                            </div>
                          )}
                        </div>
                        {canMarkMaintenance && (
                          <button
                            ref={(el) => {
                              if (el) checkButtonRefs.current.set(item.id, el);
                              else checkButtonRefs.current.delete(item.id);
                            }}
                            type="button"
                            className="icon-btn maint__row-check"
                            aria-label={`Registrar mantención de "${item.label}"`}
                            onClick={() => setServicingItem({ id: item.id, label: item.label })}
                          >
                            <Icon.Check s={14} />
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
                {maintenanceView.kind === "ready" && sortedMaintenanceItems.length > 0 && (
                  <div className="panel__foot">
                    <div className="maint__legend">
                      <span>
                        <span className="st-dot st-dot--idle" /> Sin registro
                      </span>
                      <span>
                        <span className="st-dot st-dot--crit" /> Vencido
                      </span>
                      <span>
                        <span className="st-dot st-dot--warn" /> Por vencer
                      </span>
                      <span>
                        <span className="st-dot st-dot--ok" /> Vigente
                      </span>
                    </div>
                    <span>Ordenado por urgencia.</span>
                  </div>
                )}
              </div>

              <div className="panel">
                <div className="panel__head">
                  <div className="panel__title">Historial de reportes</div>
                  <div className="panel__sub mono">
                    {/* «Últimas n»: byAssetVisits is capped server-side
                      (VISITS_PER_ASSET), so this is a window, not the
                      store's total. */}
                    {visits.length === 0
                      ? "Sin actas reportadas"
                      : visits.length === 1
                        ? `Última acta ${closedActaAdjective(recipe, false)}`
                        : `Últimas ${visits.length} actas ${closedActaAdjective(recipe, true)}`}
                  </div>
                </div>
                <div className="panel__body">
                  {visits.length === 0 ? (
                    <div className="empty empty--visitas">
                      <div className="empty__title">Aún no hay reportes cerrados</div>
                      <div className="empty__sub">
                        Cuando se cierre el primer reporte de esta tienda, aparecerá aquí.
                      </div>
                    </div>
                  ) : (
                    <div className="timeline">
                      {visits.map((v) => (
                        <div key={v.reportId} className={`timeline__item ${timelineTone(v.score)}`}>
                          <div className="timeline__dot" />
                          <div className="timeline__row">
                            <div className="timeline__main">
                              <div className="timeline__date">
                                {fmtDate(v.signedAt)} · {v.reportType}
                              </div>
                              <div className="timeline__title">
                                {v.score !== null ? `Score ${v.score.toFixed(2)} · ` : ""}
                                {v.findingsCount} observaciones
                              </div>
                            </div>
                            {/* Plain <a>, not <Link>: the report view lives
                              outside the Shell and opens in a new tab so
                              the manager keeps this store's context
                              (design handoff reporte_escritorio §1). */}
                            <a
                              className="btn btn--secondary btn--sm"
                              href={`/reportes/${v.reportId}`}
                              target="_blank"
                              rel="noopener"
                              aria-label={`Ir al reporte del ${fmtDate(v.signedAt)} (abre en pestaña nueva)`}
                            >
                              Ir al reporte ↗
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="panel">
              <div className="panel__head">
                <div className="panel__title">Observaciones abiertas</div>
                <div className="panel__sub mono">{openObs.length} sin cerrar</div>
                <div className="panel__actions">
                  <Link
                    to={`/observaciones?asset=${encodeURIComponent(tienda.id)}`}
                    className="btn btn--ghost btn--sm"
                  >
                    Ver cola completa <Icon.Arrow s={12} />
                  </Link>
                </div>
              </div>
              <div className="panel__body--flush">
                {openObs.length === 0 ? (
                  <div className="empty">
                    <div className="empty__title">Sin observaciones abiertas</div>
                    <div className="empty__sub">La tienda no tiene observaciones por cerrar.</div>
                  </div>
                ) : (
                  <table className="dtable">
                    <thead>
                      <tr>
                        <th>Observación</th>
                        {/* No Área column: current production behavior, kept
                            while the founder decision is pending (2026-10-22). */}
                        <th>Severidad</th>
                        <th>Evidencia</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openObs.map((o) => {
                        const headline = observationHeadline(o);
                        // Same row-link pattern as Observaciones' table:
                        // real interactive children (the Link, the Estado
                        // combobox) handle their own activation, the row
                        // onClick is a mouse-only enhancement.
                        function handleRowClick(e: React.MouseEvent<HTMLTableRowElement>) {
                          if (
                            (e.target as HTMLElement).closest(
                              'a, button, [role="button"], [role="combobox"], [role="listbox"], [role="option"], [role="dialog"]'
                            )
                          )
                            return;
                          navigate(`/observaciones/${o.id}`, { state: backState });
                        }
                        return (
                          <tr key={o.id} className="row-link" onClick={handleRowClick}>
                            <td>
                              <div className="cell-with-sub cell-with-sub--headline">
                                {/* Keyboard-reachable path to the detail (SC
                                2.1.1); the full description rides along
                                sr-only so AT hears what a sighted user
                                would read after clicking through. */}
                                <Link
                                  to={`/observaciones/${o.id}`}
                                  state={backState}
                                  className="cell-strong cell-strong--link"
                                >
                                  {headline.text}
                                  {headline.fullText && headline.fullText !== headline.text && (
                                    <span className="sr-only"> — {headline.fullText}</span>
                                  )}
                                </Link>
                                <span className="cell-sub">
                                  {daysSinceLabel(o.findingObservedAt ?? o.findingCreatedAt)}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className={`st-pill ${sevPillClass(o.severity)}`}>
                                {sevLabel(o.severity)}
                              </span>
                            </td>
                            <td>
                              <EvidenciaCell
                                count={o.evidenceCount}
                                thumbUrls={o.evidenceThumbUrls}
                              />
                            </td>
                            <td>
                              {/* The thread's state, read-only: the
                              actions (assign, anular, the verdict) live
                              in the observation's detail, one click
                              away, with their mandatory reasons. */}
                              <EstadoPills state={entryState(o, recipe)} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {servicingItem && (
        <MaintenanceServicePopover
          assetId={tienda.id}
          itemId={servicingItem.id}
          itemLabel={servicingItem.label}
          anchorEl={checkButtonRefs.current.get(servicingItem.id) ?? null}
          onClose={() => setServicingItem(null)}
          onServiced={onMaintenanceServiced}
        />
      )}
      {toast && (
        <div className="toast-zone">
          <div className="toast toast--ok" role="status" aria-live="polite">
            {toast}
          </div>
        </div>
      )}
    </>
  );
}

// "Última mantención: {relative time}" for a maintenance item, or a
// never-serviced empty state — formatRelativeAgo returns "" for a null
// iso, so the null case is handled here rather than passed through to
// the formatter. Unlike checklist templates' equivalent, there is no
// lastServicedSource to report: a maintenance item has exactly one
// freshness source (ADR-0083 — no acta binding at all).
function maintenanceServiceLabel(item: MaintenanceItemSummary): string {
  if (!item.lastServicedAt) return "Sin registro";
  const relative = formatRelativeAgo(item.lastServicedAt, new Date());
  return `Última mantención: ${relative}`;
}

function daysSinceLabel(iso: string): string {
  const d = daysSince(iso);
  if (d === null) return "—";
  if (d === 0) return "hoy";
  if (d === 1) return "hace 1 día";
  return `hace ${d} días`;
}
